import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { ChartBar, Users, Package, CurrencyDollar, ForkKnife, CalendarCheck, Plus, Pencil, Trash, Check, X, HeartStraight, MagnifyingGlass, Eye, Buildings, Sparkle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { AdminCateringTab } from '@/components/admin/AdminCateringTab';
import { AdminSubscriptionsTab } from '@/components/admin/AdminSubscriptionsTab';
import { AdminFitnessCentersTab } from '@/components/admin/AdminFitnessCentersTab';
import { AdminCateringMenuTab } from '@/components/admin/AdminCateringMenuTab';
import { AdminPlansEditorTab } from '@/components/admin/AdminPlansEditorTab';
import AdminWellnessTab from '@/components/admin/AdminWellnessTab';
import { getOrderStatusStyle } from '@/lib/statusColors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [meals, setMeals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subRequests, setSubRequests] = useState([]);
  const [cateringOrders, setCateringOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMealForm, setShowMealForm] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);

  const [mealForm, setMealForm] = useState({
    name: '', category: 'lunch', description: '', calories: 0, protein: 0, carbs: 0, fats: 0,
    ingredients: '', price: 0, image_url: '', is_veg: true, is_available: true
  });

  const [planForm, setPlanForm] = useState({
    name: '', type: 'monthly', duration_months: 1, price: 0, meals_per_day: 3, features: ''
  });

  const fetchAllData = useCallback(async () => {
    try {
      const [statsRes, ordersRes, mealsRes, plansRes, subsRes, subReqRes, cateringRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/corporate/orders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/meals`),
        axios.get(`${BACKEND_URL}/api/subscriptions/plans`),
        axios.get(`${BACKEND_URL}/api/admin/subscriptions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/subscription-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/catering-orders`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setMeals(mealsRes.data);
      setPlans(plansRes.data);
      setSubscriptions(subsRes.data);
      setSubRequests(subReqRes.data || []);
      setCateringOrders(cateringRes.data || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user, navigate, fetchAllData]);

  const handleUpdateCateringStatus = async (requestId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/catering-orders/${requestId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success(`Order ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleUpdateSubStatus = async (requestId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/subscription-requests/${requestId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success(`Request ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/corporate-orders/${orderId}?status=${status}`, {}, { withCredentials: true });
      toast.success(`Order ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleSaveMeal = async (e) => {
    e.preventDefault();
    const payload = {
      ...mealForm,
      ingredients: mealForm.ingredients.split(',').map(i => i.trim()).filter(Boolean),
      calories: Number(mealForm.calories),
      protein: Number(mealForm.protein),
      carbs: Number(mealForm.carbs),
      fats: Number(mealForm.fats),
      price: Number(mealForm.price),
    };

    try {
      if (editingMeal) {
        await axios.put(`${BACKEND_URL}/api/admin/meals/${editingMeal.meal_id}`, payload, { withCredentials: true });
        toast.success('Meal updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/meals`, payload, { withCredentials: true });
        toast.success('Meal created');
      }
      setShowMealForm(false);
      setEditingMeal(null);
      resetMealForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save meal');
    }
  };

  const handleDeleteMeal = async (mealId) => {
    if (!window.confirm('Delete this meal?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/meals/${mealId}`, { withCredentials: true });
      toast.success('Meal deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete meal');
    }
  };

  const handleEditMeal = (meal) => {
    setEditingMeal(meal);
    setMealForm({
      name: meal.name, category: meal.category, description: meal.description,
      calories: meal.calories, protein: meal.protein, carbs: meal.carbs, fats: meal.fats,
      ingredients: meal.ingredients.join(', '), price: meal.price, image_url: meal.image_url,
      is_veg: meal.is_veg, is_available: meal.is_available
    });
    setShowMealForm(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    const payload = {
      ...planForm,
      duration_months: Number(planForm.duration_months),
      price: Number(planForm.price),
      meals_per_day: Number(planForm.meals_per_day),
      features: planForm.features.split(',').map(f => f.trim()).filter(Boolean),
    };

    try {
      if (editingPlan) {
        await axios.put(`${BACKEND_URL}/api/admin/plans/${editingPlan.plan_id}`, payload, { withCredentials: true });
        toast.success('Plan updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/plans`, payload, { withCredentials: true });
        toast.success('Plan created');
      }
      setShowPlanForm(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name, type: plan.type, duration_months: plan.duration_months,
      price: plan.price, meals_per_day: plan.meals_per_day, features: plan.features.join(', ')
    });
    setShowPlanForm(true);
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/plans/${planId}`, { withCredentials: true });
      toast.success('Plan deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const resetMealForm = () => {
    setMealForm({ name: '', category: 'lunch', description: '', calories: 0, protein: 0, carbs: 0, fats: 0, ingredients: '', price: 0, image_url: '', is_veg: true, is_available: true });
  };

  const resetPlanForm = () => {
    setPlanForm({ name: '', type: 'monthly', duration_months: 1, price: 0, meals_per_day: 3, features: '' });
  };

  const tabs = [
    { id: 'dashboard',       label: 'Dashboard',           icon: ChartBar },
    { id: 'catering',        label: 'Corporate Catering',  icon: Buildings },
    { id: 'subscriptions',   label: 'Subscription Requests', icon: HeartStraight },
    { id: 'wellness',        label: 'Wellness Pros',        icon: Sparkle },
    { id: 'corporate-menu',  label: 'Edit Menu',            icon: ForkKnife },
    { id: 'plans-editor',    label: 'Subscription Plans',   icon: CalendarCheck },
    { id: 'orders',          label: 'Corporate Orders',     icon: Package },
    { id: 'meals',           label: 'Meal Library',         icon: ForkKnife },
    { id: 'centers',         label: 'Fitness Centers',      icon: Buildings },
    { id: 'users',           label: 'Users',                icon: Users },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <Header user={user} />
        <div className="flex items-center justify-center py-24">
          <div className="w-16 h-16 border-4 border-[#1A4331] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Header user={user} />

      <main className="px-6 md:px-12 lg:px-24 py-8" data-testid="admin-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading" data-testid="admin-heading">Admin Panel</h1>
          <p className="text-stone-600 mt-2">Manage your Nova Foods business</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-stone-200 pb-4" data-testid="admin-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all duration-200 ${
                activeTab === tab.id ? 'bg-[#1A4331] text-white' : 'bg-white border border-stone-200 text-stone-700 hover:border-[#1A4331]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} weight="duotone" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Total Users', value: stats.total_users, icon: Users, color: '#1A4331' },
                { label: 'Total Orders', value: stats.total_orders, icon: Package, color: '#C05C3D' },
                { label: 'Active Subscriptions', value: stats.active_subscriptions, icon: CalendarCheck, color: '#34D399' },
                { label: 'Total Revenue', value: `₹${stats.total_revenue?.toLocaleString() || 0}`, icon: CurrencyDollar, color: '#FBBF24' },
              ].map((stat, i) => (
                <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                  className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                      <stat.icon size={24} weight="duotone" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider">{stat.label}</p>
                      <p className="text-2xl font-bold text-[#1C1917]">{stat.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Corporate Catering Tab */}
        {activeTab === 'catering' && (
          <AdminCateringTab orders={cateringOrders} onUpdateStatus={handleUpdateCateringStatus} />
        )}

        {/* Edit Corporate Menu Tab */}
        {activeTab === 'corporate-menu' && (
          <AdminCateringMenuTab />
        )}

        {/* Subscription Plans Editor Tab */}
        {activeTab === 'plans-editor' && (
          <AdminPlansEditorTab />
        )}

        {/* Subscription Requests Tab */}
        {activeTab === 'subscriptions' && (
          <AdminSubscriptionsTab requests={subRequests} onUpdateStatus={handleUpdateSubStatus} />
        )}

        {/* Fitness Centers Tab */}
        {activeTab === 'centers' && (
          <AdminFitnessCentersTab />
        )}

        {activeTab === 'wellness' && (
          <AdminWellnessTab />
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6" data-testid="orders-management">
            <h2 className="text-xl font-medium text-[#1C1917] mb-6 font-heading">Corporate Orders</h2>
            {orders.length === 0 ? (
              <p className="text-stone-500 text-center py-8">No orders yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-200">
                      {['Company', 'Email', 'Date', 'Time', 'Meal', 'Qty', 'Amount', 'Status', 'Actions'].map(h => (
                        <th key={h} className="text-left py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.order_id} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-3 px-3 text-sm font-medium text-[#1C1917]">{order.company_name}</td>
                        <td className="py-3 px-3 text-sm text-stone-600">{order.contact_email}</td>
                        <td className="py-3 px-3 text-sm text-stone-600">{order.event_date}</td>
                        <td className="py-3 px-3 text-sm text-stone-600">{order.event_time}</td>
                        <td className="py-3 px-3 text-sm text-stone-600 capitalize">{order.meal_type?.replace('-', ' ')}</td>
                        <td className="py-3 px-3 text-sm text-stone-600">{order.quantity}</td>
                        <td className="py-3 px-3 text-sm font-medium text-[#1A4331]">₹{order.total_price?.toLocaleString()}</td>
                        <td className="py-3 px-3">
                          <span data-testid={`order-status-badge-${order.order_id}`} className={`text-xs px-3 py-1 rounded-full font-medium ${getOrderStatusStyle(order.status)}`}>{order.status}</span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-2">
                            {order.status === 'pending' && (
                              <>
                                <button onClick={() => handleUpdateOrderStatus(order.order_id, 'approved')} className="text-green-600 hover:bg-green-50 p-1.5 rounded-lg" title="Approve" data-testid={`approve-${order.order_id}`}>
                                  <Check size={18} weight="bold" />
                                </button>
                                <button onClick={() => handleUpdateOrderStatus(order.order_id, 'cancelled')} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg" title="Cancel" data-testid={`cancel-${order.order_id}`}>
                                  <X size={18} weight="bold" />
                                </button>
                              </>
                            )}
                            {order.status === 'approved' && (
                              <button onClick={() => handleUpdateOrderStatus(order.order_id, 'completed')} className="text-xs bg-stone-700 text-white px-3 py-1 rounded-full hover:bg-stone-600">Complete</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Meals Tab */}
        {activeTab === 'meals' && (
          <div className="space-y-6" data-testid="meals-management">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-medium text-[#1C1917] font-heading">Menu Management</h2>
              <button onClick={() => { resetMealForm(); setEditingMeal(null); setShowMealForm(true); }}
                className="bg-[#1A4331] text-white px-5 py-2.5 rounded-full font-medium hover:bg-[#2A5A44] transition-all flex items-center gap-2" data-testid="add-meal-btn">
                <Plus size={18} weight="bold" /> Add Meal
              </button>
            </div>

            {showMealForm && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6" data-testid="meal-form">
                <h3 className="text-lg font-medium mb-4 font-heading">{editingMeal ? 'Edit Meal' : 'Create New Meal'}</h3>
                <form onSubmit={handleSaveMeal} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Name</label>
                    <input type="text" value={mealForm.name} onChange={(e) => setMealForm({...mealForm, name: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required data-testid="meal-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Category</label>
                    <select value={mealForm.category} onChange={(e) => setMealForm({...mealForm, category: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" data-testid="meal-category-select">
                      <option value="fruit-salad">Fruit Salad</option>
                      <option value="pre-workout">Pre-Workout</option>
                      <option value="post-workout">Post-Workout</option>
                      <option value="lunch">Lunch</option>
                      <option value="evening">Evening</option>
                      <option value="dinner">Dinner</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Description</label>
                    <input type="text" value={mealForm.description} onChange={(e) => setMealForm({...mealForm, description: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Calories</label>
                    <input type="number" value={mealForm.calories} onChange={(e) => setMealForm({...mealForm, calories: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Protein (g)</label>
                    <input type="number" value={mealForm.protein} onChange={(e) => setMealForm({...mealForm, protein: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Carbs (g)</label>
                    <input type="number" value={mealForm.carbs} onChange={(e) => setMealForm({...mealForm, carbs: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Fats (g)</label>
                    <input type="number" value={mealForm.fats} onChange={(e) => setMealForm({...mealForm, fats: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Price (₹)</label>
                    <input type="number" value={mealForm.price} onChange={(e) => setMealForm({...mealForm, price: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Image URL</label>
                    <input type="text" value={mealForm.image_url} onChange={(e) => setMealForm({...mealForm, image_url: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Ingredients (comma separated)</label>
                    <input type="text" value={mealForm.ingredients} onChange={(e) => setMealForm({...mealForm, ingredients: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" placeholder="Rice, Dal, Vegetables..." required />
                  </div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={mealForm.is_veg} onChange={(e) => setMealForm({...mealForm, is_veg: e.target.checked})} className="w-4 h-4" />
                      <span className="text-sm text-stone-700">Vegetarian</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={mealForm.is_available} onChange={(e) => setMealForm({...mealForm, is_available: e.target.checked})} className="w-4 h-4" />
                      <span className="text-sm text-stone-700">Available</span>
                    </label>
                  </div>
                  <div className="md:col-span-2 flex gap-3">
                    <button type="submit" className="bg-[#1A4331] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#2A5A44]" data-testid="save-meal-btn">
                      {editingMeal ? 'Update Meal' : 'Create Meal'}
                    </button>
                    <button type="button" onClick={() => { setShowMealForm(false); setEditingMeal(null); }} className="border border-stone-300 text-stone-700 px-6 py-2.5 rounded-full font-medium hover:bg-stone-100">Cancel</button>
                  </div>
                </form>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {meals.map((meal) => (
                <div key={meal.meal_id} className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden" data-testid={`admin-meal-${meal.meal_id}`}>
                  {meal.image_url && <img src={meal.image_url} alt={meal.name} className="w-full h-32 object-cover" />}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-[#1C1917]">{meal.name}</h3>
                      <div className="flex gap-1">
                        {meal.is_veg && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">VEG</span>}
                        {!meal.is_available && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Unavailable</span>}
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-2 capitalize">{meal.category.replace('-', ' ')}</p>
                    <div className="flex gap-3 text-xs text-stone-600 mb-3">
                      <span>{meal.calories} cal</span>
                      <span>{meal.protein}g P</span>
                      <span>{meal.carbs}g C</span>
                      <span>{meal.fats}g F</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-[#1A4331]">₹{meal.price}</span>
                      <div className="flex gap-2">
                        <button onClick={() => handleEditMeal(meal)} className="text-stone-600 hover:text-[#1A4331] p-1.5 rounded-lg hover:bg-stone-100" data-testid={`edit-meal-${meal.meal_id}`}>
                          <Pencil size={16} weight="duotone" />
                        </button>
                        <button onClick={() => handleDeleteMeal(meal.meal_id)} className="text-stone-600 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-meal-${meal.meal_id}`}>
                          <Trash size={16} weight="duotone" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="space-y-6" data-testid="plans-management">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-medium text-[#1C1917] font-heading">Subscription Plans</h2>
              <button onClick={() => { resetPlanForm(); setEditingPlan(null); setShowPlanForm(true); }}
                className="bg-[#1A4331] text-white px-5 py-2.5 rounded-full font-medium hover:bg-[#2A5A44] transition-all flex items-center gap-2" data-testid="add-plan-btn">
                <Plus size={18} weight="bold" /> Add Plan
              </button>
            </div>

            {showPlanForm && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6" data-testid="plan-form">
                <h3 className="text-lg font-medium mb-4 font-heading">{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h3>
                <form onSubmit={handleSavePlan} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Plan Name</label>
                    <input type="text" value={planForm.name} onChange={(e) => setPlanForm({...planForm, name: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required data-testid="plan-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Type</label>
                    <select value={planForm.type} onChange={(e) => setPlanForm({...planForm, type: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" data-testid="plan-type-select">
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half-yearly">Half-Yearly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Duration (months)</label>
                    <input type="number" value={planForm.duration_months} onChange={(e) => setPlanForm({...planForm, duration_months: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Price (₹)</label>
                    <input type="number" value={planForm.price} onChange={(e) => setPlanForm({...planForm, price: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Meals Per Day</label>
                    <input type="number" value={planForm.meals_per_day} onChange={(e) => setPlanForm({...planForm, meals_per_day: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Features (comma separated)</label>
                    <input type="text" value={planForm.features} onChange={(e) => setPlanForm({...planForm, features: e.target.value})} className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A4331]" placeholder="3 meals/day, AI planning..." required />
                  </div>
                  <div className="md:col-span-2 flex gap-3">
                    <button type="submit" className="bg-[#1A4331] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#2A5A44]" data-testid="save-plan-btn">
                      {editingPlan ? 'Update Plan' : 'Create Plan'}
                    </button>
                    <button type="button" onClick={() => { setShowPlanForm(false); setEditingPlan(null); }} className="border border-stone-300 text-stone-700 px-6 py-2.5 rounded-full font-medium hover:bg-stone-100">Cancel</button>
                  </div>
                </form>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => (
                <div key={plan.plan_id} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-5" data-testid={`admin-plan-${plan.plan_id}`}>
                  <h3 className="font-medium text-[#1C1917] mb-1">{plan.name}</h3>
                  <p className="text-2xl font-bold text-[#1A4331] mb-2">₹{plan.price?.toLocaleString()}<span className="text-sm text-stone-500 font-normal">/{plan.type}</span></p>
                  <p className="text-sm text-stone-600 mb-3">{plan.meals_per_day} meals/day for {plan.duration_months} months</p>
                  <ul className="text-xs text-stone-600 space-y-1 mb-4">
                    {plan.features?.map((f) => <li key={f}>- {f}</li>)}
                  </ul>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditPlan(plan)} className="flex-1 text-sm border border-stone-300 text-stone-700 py-1.5 rounded-full hover:bg-stone-100 flex items-center justify-center gap-1">
                      <Pencil size={14} /> Edit
                    </button>
                    <button onClick={() => handleDeletePlan(plan.plan_id)} className="text-sm border border-red-200 text-red-500 py-1.5 px-3 rounded-full hover:bg-red-50">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6" data-testid="users-management">
            <h2 className="text-xl font-medium text-[#1C1917] mb-6 font-heading">Users Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-stone-50 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Total Users</p>
                <p className="text-3xl font-bold text-[#1C1917]">{stats?.total_users || 0}</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Active Subscriptions</p>
                <p className="text-3xl font-bold text-[#34D399]">{stats?.active_subscriptions || 0}</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Total Meals</p>
                <p className="text-3xl font-bold text-[#C05C3D]">{stats?.total_meals || 0}</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
